import type { CreditTermsInput } from '@optiflow/types';

/**
 * Credit terms as a form holds them, and as a checkout sends them.
 *
 * The server builds the schedule and decides the limit; these checks only mirror its field rules so a typo is caught before the request. A field left blank takes the tenant's default.
 */
export interface CreditTermsDraft {
  installmentCount: string;
  /** `YYYY-MM-DD`, or empty for the tenant's default. */
  firstDueDate: string;
  interval: 'MONTHLY' | 'WEEKLY' | 'DAYS';
  intervalDays: string;
  overrideReason: string;
}

export const EMPTY_CREDIT_TERMS: CreditTermsDraft = {
  installmentCount: '1',
  firstDueDate: '',
  interval: 'MONTHLY',
  intervalDays: '30',
  overrideReason: '',
};

const WHOLE = /^\d{1,3}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Field problems as i18n keys, by field. */
export function creditTermsProblems(
  terms: CreditTermsDraft,
  maxInstallments: number,
  overrideRequired: boolean,
): Record<string, string> {
  const problems: Record<string, string> = {};
  const count = terms.installmentCount.trim();
  if (!WHOLE.test(count) || Number(count) < 1 || Number(count) > Math.min(60, maxInstallments)) {
    problems.installmentCount = 'sales.creditTerms.installmentCountInvalid';
  }
  if (terms.interval === 'DAYS') {
    const days = terms.intervalDays.trim();
    if (!WHOLE.test(days) || Number(days) < 1 || Number(days) > 365) {
      problems.intervalDays = 'sales.creditTerms.intervalDaysInvalid';
    }
  }
  if (terms.firstDueDate.trim() !== '' && !DATE.test(terms.firstDueDate.trim())) {
    problems.firstDueDate = 'sales.creditTerms.firstDueDateInvalid';
  }
  if (overrideRequired && terms.overrideReason.trim().length < 3) {
    problems.overrideReason = 'sales.creditTerms.overrideReasonRequired';
  }
  return problems;
}

export function creditTermsInput(terms: CreditTermsDraft): CreditTermsInput {
  const date = terms.firstDueDate.trim();
  const reason = terms.overrideReason.trim();
  return {
    installmentCount: Number(terms.installmentCount.trim()),
    ...(date !== '' ? { firstDueDate: date } : {}),
    interval: terms.interval,
    ...(terms.interval === 'DAYS' ? { intervalDays: Number(terms.intervalDays.trim()) } : {}),
    ...(reason !== '' ? { overrideReason: reason } : {}),
  };
}
